import React from 'react';
import { AreaChartOutlined, BookOutlined, CommentOutlined, LogoutOutlined } from '@ant-design/icons';
import IconRound from 'assets/logos/icon-round.svg?react';
import { useAuth } from 'auth/AuthContext';
import { Button } from 'components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from 'components/ui/navigation-menu';
import useIsSmallScreen from 'hooks/useIsSmallScreen';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { RouteInfo, routes } from '../../services/RouteService';

export default function BottomNavBar() {
  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { isSmallScreen } = useIsSmallScreen();
  const location = useLocation();

  const onClick = (route: string) => {
    if (location.pathname === route) {
      // if we are already on the route, scroll to top
      navigate(route, { replace: true, state: { ...location.state, scrollValue: 0 } });
    } else {
      navigate(route);
    }
  };

  const matchingRoute = Object.values(routes).find(route => matchPath(location.pathname, route.route));

  function renderIcon(svgIcon: React.ReactElement, active: boolean) {
    // Inactive icons are turquoise (`primary`) and the active one the deeper
    // mint (`success-text`); the labels pair the other way round (see below) so
    // the active label stays the one text that reads as dark/saturated.
    return (
      // `aria-hidden`: every nav item already renders its label as text, so the
      // icon is purely decorative. antd icons carry an `aria-label` of their own
      // ("comment", "area-chart", "book"), which without this gets folded into
      // the link's accessible name — screen readers announce "comment
      // Messages", and `get_by_role("link", name="OK")` starts matching
      // "book M'informer".
      //
      // `NavigationMenuLink` forces every unsized / uncoloured `svg` to 16px
      // muted-foreground; the icons here are sized in `em` and inherit the
      // colour of this wrapper instead.
      <span
        aria-hidden
        className={cn(
          'flex items-center justify-center [&_svg:not([class*=size-])]:size-[1em]! [&_svg]:text-current!',
          active ? 'text-success-text' : 'text-primary',
          isSmallScreen ? 'h-8 text-2xl' : 'h-9 text-3xl',
        )}
      >
        {React.cloneElement(svgIcon as React.ReactElement<{ fill?: string }>, { fill: 'currentColor' })}
      </span>
    );
  }

  function renderNavItem(params: { svgIcon: React.ReactElement; routeInfo: RouteInfo; routesGroup: string }) {
    const isActive = matchingRoute?.routesGroup === params.routesGroup;

    return (
      // The items must be allowed to shrink: a flex item defaults to
      // `min-width: auto`, so without `min-w-0` the labels set the floor and
      // four of them overflow a 390px viewport.
      <NavigationMenuItem className="min-w-0 shrink">
        <NavigationMenuLink
          asChild
          active={isActive}
          className={cn(
            'items-center hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent',
            isSmallScreen ? 'flex-col gap-0.5' : 'flex-row gap-1.5',
            isActive ? 'text-foreground data-[active=true]:text-foreground' : 'text-success-text',
          )}
        >
          <Link
            to={params.routeInfo.route}
            onClick={event => {
              event.preventDefault();
              onClick(params.routeInfo.route);
            }}
          >
            {renderIcon(params.svgIcon, isActive)}
            <span className={isSmallScreen ? 'text-xs' : 'text-sm'}>{params.routeInfo.label}</span>
          </Link>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  return (
    // Radix wraps the list in a positioning `div`, which is why that child
    // needs to be stretched too for the items to spread across the bar.
    <NavigationMenu
      viewport={false}
      className="w-full max-w-none shrink-0 bg-card px-2 pt-2 pb-[calc(var(--spacing-safe-bottom)/2+--spacing(5))] *:w-full"
    >
      <NavigationMenuList className="h-bottom-nav w-full justify-around">
        {renderNavItem({
          svgIcon: <IconRound className="size-5" />,
          routeInfo: routes.home,
          routesGroup: 'home',
        })}
        {renderNavItem({
          svgIcon: <CommentOutlined />,
          routeInfo: routes.messagingHome,
          routesGroup: 'messaging',
        })}
        {renderNavItem({
          routeInfo: routes.symptomTracking,
          routesGroup: 'tracking',
          svgIcon: <AreaChartOutlined />,
        })}
        {renderNavItem({
          svgIcon: <BookOutlined className="text-[0.9em]" />,
          routeInfo: routes.info,
          routesGroup: 'info',
        })}
        {!isSmallScreen && (
          <NavigationMenuItem className="min-w-0 shrink">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('menu.logout')}
              className="text-success-text [&_svg:not([class*=size-])]:size-[1em]!"
              onClick={() => {
                logout();
              }}
            >
              <LogoutOutlined aria-hidden className="mt-1 text-2xl" />
            </Button>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
